// Mainly based on:
// https://gist.github.com/alfonmga/9602085094651c03cd2e270da9b2e3f7
// https://github.com/apollographql/apollo-link/issues/646
import { ApolloLink } from '@apollo/client/link/core';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';

import { getSession } from '../../src/services/auth';
import { refreshAccessToken, forceLogout } from './AutoRefreshToken';
import {
  writeToConsole,
  captureError,
  buildNetworkErrorDetails,
  buildGraphQLErrorDetails,
  buildUnknownError
} from './ApolloErrorUtil';

const handleExpiredToken = (operation, forward) => {
  let isFetchingToken = false;
  let tokenSubscribers = [];

  const subscribeTokenRefresh = (cb) => {
    tokenSubscribers.push(cb);
  };

  const onTokenRefreshed = (err) => {
    tokenSubscribers.map(cb => cb(err));
  };

  return new Observable(async observer => {
    const { rememberMe } = getSession();
    if (!rememberMe) {
      const error = new Error("Encountered expired authenticated session, log-in is required");
      error.isSessionExpired = true;
      observer.error(error);
      return forceLogout();
    }

    try {
      const retryRequest = (newAccessToken) => {
        operation.setContext({
          headers: {
            ...headers,
            Authorization: `Bearer ${newAccessToken}`,
          },
        });

        const subscriber = {
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        };

        return forward(operation).subscribe(subscriber);
      };

      const { headers } = operation.getContext();

      if (!isFetchingToken) {
        isFetchingToken = true;

        try {
          const newAccessToken = await refreshAccessToken();

          isFetchingToken = false;
          onTokenRefreshed(null);
          tokenSubscribers = [];

          return retryRequest(newAccessToken);
        } catch (e) {
          const error = new Error(`Unable to refresh access token, log-in is required: ${e.message}`);
          observer.error(error);
          onTokenRefreshed(error);

          tokenSubscribers = [];
          isFetchingToken = false;

          return forceLogout();
        }
      }

      return new Promise((resolve, reject) => {
        subscribeTokenRefresh(errRefreshing => {
          if (errRefreshing) return reject(errRefreshing);

          const { accessToken } = getSession();
          return resolve(retryRequest(accessToken));
        });
      });
    } catch (e) {
      writeToConsole(e, 'error');
      observer.error(e);
    }
  });
};

const handleInvalidAuthorizationHeader = (authError) => {
    return new Observable(async observer => {
      const error = new Error(`Encountered invalid authorization header, log-in is required: ${authError.message}`);
      error.isSessionExpired = true;
      observer.error(error);
      return forceLogout();
    });
};

const handleAuthenticationError = (authError, operation, forward) => {
  const { reason } = authError.details;
  writeToConsole(authError, 'warn');

  const result = reason === 'expired_token'
    ? handleExpiredToken(operation, forward)
    : handleInvalidAuthorizationHeader(authError);

  if (result?.subscribe) {
    return new Observable(observer => {
      return result.subscribe({
        ...observer,
        error: (error) => {
          if (error?.isSessionExpired) {
            writeToConsole('Handled session expired error, skipping reporting to Sentry', 'debug');
            return;
          }

          writeToConsole('Reporting other authentication error to Sentry', 'debug');
          observer.error(error);
        }
      });
    });
  }

  return result;
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (networkError) {
    captureError(buildNetworkErrorDetails(networkError, operation));
  }

  if (graphQLErrors) {
    const errors = graphQLErrors.map(graphQLError => buildGraphQLErrorDetails(graphQLError, operation));

    // Check for authentication error first and return early if found since it needs special handling
    const authError = errors.find(err => err.category === 'AuthenticationError');
    if (authError) return handleAuthenticationError(authError, operation, forward);

    errors.forEach(error => captureError(error));
  }
});

const fixMalformedErrors = (errors) => {
  return (errors || []).map(error => {
    if (!error.message) return buildUnknownError(error);
    return error;
  });
};

const responseErrorNormalizerLink = new ApolloLink((operation, forward) => {
  return forward(operation).map(response => {
    return {
      ...response,
      errors: fixMalformedErrors(response.errors)
    };
  });
});

export { errorLink, responseErrorNormalizerLink };